import { useEffect, useState } from "react";
import { FloppyDisk, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../lib/permissions";

const empty = {
  name: "",
  tax_id: "",
  address: "",
  email: "",
};

export default function DatosLegales() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.MANAGE_SETTINGS);

  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get("/legal/settings");
        setData({ ...empty, ...res.data });
      } catch (err) {
        toast.error("No se pudieron cargar los datos legales");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const onChange = (field) => (event) => {
    setData((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit || saving) return;

    setSaving(true);

    try {
      const res = await api.put("/legal/settings", data);
      setData({ ...empty, ...res.data });
      toast.success("Datos legales guardados");
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="py-8 text-med-muted">Cargando datos legales…</p>;
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-med-surface border border-med-border rounded-2xl p-6 md:p-8 max-w-3xl"
      data-testid="legal-settings-form"
    >
      {!canEdit && (
        <div className="mb-6 rounded-xl border border-med-warning/30 bg-med-warning/10 text-med-warning px-4 py-3 text-sm">
          No tienes permiso para modificar estos datos.
        </div>
      )}

      <div className="flex items-start gap-3 mb-6">
        <ShieldCheck
          size={28}
          weight="duotone"
          className="text-med-primary shrink-0"
        />
        <div>
          <h2 className="font-display text-xl font-bold text-med-text">
            Responsable del tratamiento
          </h2>
          <p className="text-sm text-med-muted mt-1">
            Estos datos se mostrarán en la política de privacidad de la
            organización.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Nombre o razón social *"
          value={data.name}
          onChange={onChange("name")}
          disabled={!canEdit}
          required
        />
        <Field
          label="NIF / CIF *"
          value={data.tax_id}
          onChange={onChange("tax_id")}
          disabled={!canEdit}
          required
        />
        <div className="md:col-span-2">
          <Field
            label="Domicilio legal *"
            value={data.address}
            onChange={onChange("address")}
            disabled={!canEdit}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Correo de contacto *"
            value={data.email}
            onChange={onChange("email")}
            disabled={!canEdit}
            required
            type="email"
          />
        </div>
      </div>

      <p className="mt-5 text-xs text-med-muted">
        Comprueba que estos datos son correctos y están actualizados antes de
        publicar la política de privacidad.
      </p>

      <div className="flex justify-end mt-8 pt-6 border-t border-med-border">
        <button
          type="submit"
          disabled={!canEdit || saving}
          className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors"
        >
          <FloppyDisk size={18} weight="bold" />
          {saving ? "Guardando…" : "Guardar datos legales"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, disabled, required, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-colors disabled:bg-med-surface2 disabled:cursor-not-allowed"
      />
    </div>
  );
}
